import React, {useEffect, useState} from 'react'
import { Box,Button, TextField, Typography, Autocomplete, useMediaQuery, useTheme } from '@mui/material'
import { useAppDispatch } from '../../../../redux/storeExports'
import { createExportLookerReportApi, ExportEmailRequest, CorpCodesApi, createExportCorporateReportApi, ExportCorporateReportRequest } from '../../../../redux/corporateCodeService'
import { validateEmail } from '../../../../utils/helpers'

enum ExportType {
    Individual = 'individual',
    CorporateCodes = 'corporate_codes',
    StripeCodes = 'stripe_codes',
}

enum CodeType {
    Corporate = 'corp_codes',
    Stripe = 'stripe_codes',
}

    const ExportReportsTab=()=>{
        const theme = useTheme()
        const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
        const [codes, setCodes] = useState<Record<CodeType, string[]>>({
            [CodeType.Corporate]: [],
            [CodeType.Stripe]: [],
        })
        const [formData, setFormData] = useState<{
            type: ExportType;
            value: string;
            emails: string[];
            ccEmails: string[];
            bccEmails: string[];
        }>({
            type: ExportType.Individual,
            value: '',
            emails: [],
            ccEmails: [],
            bccEmails: [],
        })
        const [isLoading, setIsLoading] = useState(false)
        const dispatch = useAppDispatch()

        useEffect(()=>{
            const fetchCodes = async () => {
                const codes : Record<CodeType, string[]> = await dispatch(CorpCodesApi(true)).unwrap()
                setCodes(codes)
            }
            fetchCodes()
        },[dispatch])

        const [errors, setErrors] = useState({
          'to':false,
          'cc':false,
          'bcc':false
        })
        
        const onExport = async() =>{
            setIsLoading(true)
            if (formData.type === ExportType.Individual){
                const key = validateEmail(formData.value) ? 'email' : 'muid'
                const params : ExportEmailRequest = {
                    search_key : key,
                    search_value : formData.value,
                    emails : formData.emails,
                    cc_emails : formData.ccEmails,
                    bcc_emails :  formData.bccEmails,
                }
                await dispatch(createExportLookerReportApi(params))
            }
            else{
                const params : ExportCorporateReportRequest = {
                    code: formData.value,
                    is_stripe: formData.type === ExportType.StripeCodes,
                    email: formData.emails[0],
                    cc_emails: formData.ccEmails,
                    bcc_emails: formData.bccEmails,
                }
                await dispatch(createExportCorporateReportApi(params))
            }
            setIsLoading(false)
        }

        return(
            <Box sx={{display:'flex', alignItems:'center', justifyContent:'center', width:'100%', flexDirection:'column', minHeight:'50vh'}}>                
                <Typography variant='h2'sx={{mb:7}}>Export User Report PDF</Typography>
                <Autocomplete
                    options={Object.values(ExportType)}
                    getOptionLabel={(option) => {
                        return option.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                    }}
                    value={formData.type}
                    onChange={(event, newValue) => {
                        setFormData(prev => ({
                            ...prev,
                            type: newValue as ExportType,
                            value: '',
                        }));
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label="*Export Type" variant="outlined" />
                    )}
                    sx={{ width: isMobile?'100%':'50%', mb:5}}
                />    
                {formData.type === ExportType.Individual ? (
                    <TextField
                        value={formData.value}
                        onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                        label="*Export by Muid or Email"
                        size="small"
                        sx={{width: isMobile?'100%':'50%', mb:5}}
                    />
                ):(
                  <Autocomplete
                    options={codes[formData.type === ExportType.CorporateCodes ? CodeType.Corporate : CodeType.Stripe]}
                    value={formData.value || null}
                    onChange={(event, newValue) => setFormData(prev => ({ ...prev, value: (newValue as string) || '' }))}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label= {formData.type === ExportType.CorporateCodes ? "*Corporate Code" : "*Stripe Code"} 
                            variant="outlined" 
                        />
                    )}
                    sx={{ width: isMobile?'100%':'50%', mb:5}}
                  />
                )}   
                <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={formData.emails}
                    onChange={(event, newValue) =>{ 
                        let isValid = true
                        if(newValue.length > formData.emails.length){
                            isValid = validateEmail(newValue[newValue.length -1])
                        }
                        if(isValid){
                            setFormData(prev => ({ ...prev, emails: newValue })); 
                            setErrors({
                                ...errors,
                                to: false
                            })
                            return
                        }
                        else setErrors({
                        ...errors,
                        to: true
                        })
                        }}
                    renderInput={(params) => (
                        <TextField {...params} label="*To Email"
                    error={errors['to']}
                    helperText={errors['to']? "Enter a valid email address":""}
                    {...params}
                    variant="outlined"
                    />
                    )}
                    sx={{ width: isMobile?'100%':'50%'}}
                    isOptionEqualToValue={(option, value) => option === value}
                />

                <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.ccEmails}
                onChange={(event, newValue) =>{ 
                        let isValid = true
                        if(newValue.length > formData.ccEmails.length){
                            isValid = validateEmail(newValue[newValue.length -1])
                        }
                        if(isValid){
                            setFormData(prev => ({ ...prev, ccEmails: newValue })); 
                            setErrors({
                                ...errors,
                                cc: false
                            })
                            return
                        }
                        else setErrors({
                        ...errors,
                        cc: true
                        })
                        }}
                renderInput={(params) => (
                    <TextField {...params} label="Cc"
                    error={errors['cc']}
                    helperText={errors['cc']? "Enter a valid email address":""}
                    {...params}
                    variant="outlined"
                    />
                )}
                sx={{ width:isMobile?'100%':'50%', mt: 7 }}
                isOptionEqualToValue={(option, value) => option === value}
                />

                <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.bccEmails}
                  onChange={(event, newValue) =>{ 
                        let isValid = true
                        if(newValue.length > formData.bccEmails.length){
                            isValid = validateEmail(newValue[newValue.length -1])
                        }
                        if(isValid){
                            setFormData(prev => ({ ...prev, bccEmails: newValue }));
                            setErrors({
                                ...errors,
                                bcc: false
                                })
                            return
                        }
                        else setErrors({
                        ...errors,
                        bcc: true
                        })
                        }}
                renderInput={(params) => (
                    <TextField {...params} label="Bcc"
                    error={errors['bcc']}
                    helperText={errors['bcc']? "Enter a valid email address":""}
                    {...params}
                    variant="outlined"
                    />
                )}
                sx={{ width: isMobile?'100%':'50%', mt:7}}
                isOptionEqualToValue={(option, value) => option === value}
                />
                <Button
                    variant={'contained'}
                    type={'button'}
                    disabled={isLoading || !formData.value || formData.emails.length===0}
                    sx={{width:isMobile?'40%':'15%',border:'1px green solid', backgroundColor:"#fff", borderRadius:2, color:'success.main', p:3, mt:7,
                        '&:hover': {
                            backgroundColor:'success.main',
                            color:'#fff'
                            },
                         "&.Mui-disabled": {
                            backgroundColor: "#fff",
                            color: "#c0c0c0",
                            border:'1px #c0c0c0 solid'
                            },
                    }}

                    onClick={()=>{
                        onExport()
                    }}
                >
                    <Typography variant='h4' sx={{fontSize:'16px'}}>Export</Typography>
                </Button>
            </Box>
            )
}

export default ExportReportsTab
